import { cloneElement, RefObject, useCallback, useMemo } from 'react';

import styled from 'styled-components';

import { Button } from '@components/button';
import { Card } from '@components/card';
import { EmptyState } from '@components/empty-state';
import { IconAI } from '@components/icons/ai';
import { IconArrowRight } from '@components/icons/arrow-right';
import { IconTemplate } from '@components/icons/template';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { useScopedTranslate } from '@locale/translate';
import { useSelectionStore } from '@stores/create-selection-store';
import { theme } from '@styles/theme';
import { pxToRem } from '@utils/converters';

import type { AIBuilderExamplePrompt, QuestionAnswer } from 'api/ai-builder/requests';

import { MessageItem } from './messages';

import { type ChatMessage } from '../hooks';
import { LoadingStateDisplay } from '../loading-state-display';

const DEFAULT_INPUT_ROW_HEIGHT = 72;
const MESSAGE_GAP = pxToRem(12);
const NON_RAIL_TOOL_NAMES = ['enter_plan_mode', 'exit_plan_mode', 'show_portal_link'];

type MessagesContainerProps = {
  messages: ChatMessage[];
  isGenerating: boolean;
  messagesRef: RefObject<HTMLDivElement>;
  messagesInnerRef: RefObject<HTMLDivElement>;
  onScroll: () => void;
  onPauseAutoScroll: () => void;
  inputRowHeight: number;
  onSubmitAnswers: (answers: QuestionAnswer[]) => Promise<void>;
  onPickResource: (selection: { id: string; name: string }) => Promise<void>;
  onAcceptPlan: () => Promise<void>;
  onApproveEnterPlanMode: () => Promise<void>;
  onRejectEnterPlanMode: () => Promise<void>;
  onSelectExamplePrompt: (prompt: string) => void;
  onOpenExamples: () => void;
  examplePrompts: AIBuilderExamplePrompt[];
  isPlanLoading?: boolean;
  isModeLoading?: boolean;
};

export const MessagesContainer = ({
  messages,
  isGenerating,
  messagesRef,
  messagesInnerRef,
  inputRowHeight,
  onScroll,
  onPauseAutoScroll,
  onSubmitAnswers,
  onPickResource,
  onAcceptPlan,
  onApproveEnterPlanMode,
  onRejectEnterPlanMode,
  onSelectExamplePrompt,
  onOpenExamples,
  examplePrompts,
  isPlanLoading,
  isModeLoading,
}: MessagesContainerProps) => {
  const { t } = useScopedTranslate(locale);
  const { toggle: toggleDescription, isSelected: hasDescriptionOpen } = useSelectionStore();
  const handleScroll = useCallback(() => {
    onScroll();
  }, [onScroll]);

  const featuredExamplePrompts = useMemo(() => {
    if (examplePrompts.length <= 3) {
      return examplePrompts;
    }

    const prompts = [...examplePrompts];
    for (let idx = prompts.length - 1; idx > 0; idx -= 1) {
      const randomIdx = Math.floor(Math.random() * (idx + 1));
      [prompts[idx], prompts[randomIdx]] = [prompts[randomIdx], prompts[idx]];
    }

    return prompts.slice(0, 3);
  }, [examplePrompts]);

  const allMessages = messages;

  const bottomOffsetPx = allMessages.length === 0 ? DEFAULT_INPUT_ROW_HEIGHT : inputRowHeight;
  const bottomOffset = pxToRem(bottomOffsetPx + 24);

  const handleToggleDescription = (id: string) => {
    onPauseAutoScroll();
    toggleDescription(id);
  };

  const isRailToolMessage = (message?: ChatMessage) => {
    if (!message || message.role !== 'tool') return false;
    return !NON_RAIL_TOOL_NAMES.includes(message.toolName ?? '');
  };

  return (
    <StyledMessagesContainer
      ref={messagesRef}
      onScroll={handleScroll}
      style={{
        bottom: bottomOffset,
        justifyContent: allMessages.length === 0 ? 'center' : 'flex-start',
      }}
    >
      <MessagesInner ref={messagesInnerRef} $hasMessages={allMessages.length > 0}>
        {allMessages.length === 0 ? (
          <EmptyStateContainer>
            <EmptyState
              header={t('buildWithAI')}
              content={t('description')}
              orientation="vertical"
              variant="small"
              skipMargin
              skipPadding
              transparent
              icon={<IconAI color="aiGradientTeal100" />}
              iconShadow={cloneElement(<IconAI color="aiGradientTeal100" />, {
                opacity: 0.1,
                size: 240,
              })}
            />
            {featuredExamplePrompts.length > 0 && (
              <ExamplesContainer>
                {featuredExamplePrompts.map((examplePrompt) => (
                  <Card
                    key={examplePrompt.shortId}
                    variant="default"
                    interactive
                    spaceX={2}
                    spaceY={1}
                    onClick={() => onSelectExamplePrompt(examplePrompt.prompt)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        onSelectExamplePrompt(examplePrompt.prompt);
                      }
                    }}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: theme.space[2],
                      textAlign: 'left',
                    }}
                  >
                    <IconTemplate color="textSecondary" />
                    <ExampleTextContainer>
                      <Text font="regular12" color="textSecondary" text={examplePrompt.label} />
                    </ExampleTextContainer>
                  </Card>
                ))}
                {examplePrompts.length > 3 && (
                  <Button
                    variant="ghost"
                    isFullWidth
                    font="semibold12"
                    color="textSecondary"
                    text={t('moreExamples')}
                    iconRight={<IconArrowRight color="textSecondary" size={18} />}
                    onClick={onOpenExamples}
                  />
                )}
              </ExamplesContainer>
            )}
          </EmptyStateContainer>
        ) : (
          <>
            {allMessages.map((message, idx) => {
              const isPrevTool =
                idx > 0 && isRailToolMessage(allMessages[idx - 1]) && isRailToolMessage(message);
              const isNextTool =
                idx < allMessages.length - 1 &&
                isRailToolMessage(message) &&
                isRailToolMessage(allMessages[idx + 1]);

              return (
                <MessageItem
                  key={`row-${message.id}`}
                  message={message}
                  index={idx}
                  isPrevTool={isPrevTool}
                  isNextTool={isNextTool}
                  isDescriptionOpen={hasDescriptionOpen(message.id)}
                  onToggleDescription={() => {
                    return handleToggleDescription(message.id);
                  }}
                  onSubmitAnswers={onSubmitAnswers}
                  onPickResource={onPickResource}
                  onAcceptPlan={onAcceptPlan}
                  onApproveEnterPlanMode={onApproveEnterPlanMode}
                  onRejectEnterPlanMode={onRejectEnterPlanMode}
                  isPlanLoading={isPlanLoading}
                  isModeLoading={isModeLoading}
                />
              );
            })}
            <LoadingStateDisplay isGenerating={isGenerating} />
          </>
        )}
      </MessagesInner>
    </StyledMessagesContainer>
  );
};

const StyledMessagesContainer = styled(Stack).attrs({
  space: 0,
  justify: 'flex-start',
})({
  position: 'absolute',
  left: pxToRem(16),
  right: pxToRem(16),
  top: 0,

  overflowY: 'auto',
});

const MessagesInner = styled.div<{ $hasMessages: boolean }>(({ $hasMessages }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: MESSAGE_GAP,
  '& > :first-child': {
    marginTop: $hasMessages ? pxToRem(16) : 0,
  },
}));

const EmptyStateContainer = styled(Stack).attrs({
  space: 4,
  justify: 'center',
  align: 'center',
})({
  width: '100%',
  maxWidth: pxToRem(560),
});

const ExamplesContainer = styled(Stack).attrs({
  space: 2,
})({
  width: '100%',
});

const ExampleTextContainer = styled(Stack).attrs({
  space: 2,
  align: 'flex-start',
})({
  minWidth: 0,
});

const locale = {
  en: {
    buildWithAI: 'Build with AI',
    description: 'Describe the process you want to automate, and I’ll build the workflow for you.',
    moreExamples: 'More examples',
  },
};
